/**
 * EntoRid official social media links + reusable icon group component.
 * Used across header, footer, contact page, mobile menu, and floating widget.
 */
import React from 'react';
import { Facebook, Instagram, Youtube, MessageCircle } from 'lucide-react';

export const SOCIAL_LINKS = [
  {
    key: 'facebook',
    label: 'Follow EntoRid on Facebook',
    href: 'https://www.facebook.com/profile.php?id=61582096927776',
    Icon: Facebook,
    hover: 'hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]',
    solid: 'bg-[#1877F2] text-white',
  },
  {
    key: 'instagram',
    label: 'Follow EntoRid on Instagram',
    href: 'https://www.instagram.com/ento_rid?igsh=MXd2bjg1NHBobzBxZQ==',
    Icon: Instagram,
    hover: 'hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-500 hover:to-orange-400 hover:text-white hover:border-transparent',
    solid: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white',
  },
  {
    key: 'whatsapp',
    label: 'Chat with EntoRid Expert on WhatsApp',
    href: 'https://api.whatsapp.com/send?phone=919410110910',
    Icon: MessageCircle,
    hover: 'hover:bg-[#25D366] hover:text-white hover:border-[#25D366]',
    solid: 'bg-[#25D366] text-white',
  },
  {
    key: 'youtube',
    label: 'Subscribe to EntoRid on YouTube',
    href: 'https://www.youtube.com/@EntoRid',
    Icon: Youtube,
    hover: 'hover:bg-[#FF0000] hover:text-white hover:border-[#FF0000]',
    solid: 'bg-[#FF0000] text-white',
  },
];

/**
 * Reusable social-icon row.
 * Props:
 *  - variant: 'outline' (default) | 'solid' | 'ghost'
 *  - size: 'sm' | 'md' | 'lg'  (px height: 32 / 40 / 48)
 *  - withLabel: optional small label below icons
 *  - className: extra wrapper classes
 */
export default function SocialIcons({ variant = 'outline', size = 'md', withLabel = false, className = '' }) {
  const sizePx = { sm: 'w-8 h-8', md: 'w-10 h-10', lg: 'w-12 h-12' }[size] || 'w-10 h-10';
  const iconSize = { sm: 14, md: 16, lg: 20 }[size] || 16;

  const itemClass = (s) => {
    if (variant === 'solid') return `${sizePx} ${s.solid} rounded-full flex items-center justify-center shadow-sm hover:scale-110 transition-transform duration-200`;
    if (variant === 'ghost') return `${sizePx} text-slate-300 hover:text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110`;
    return `${sizePx} bg-white text-slate-600 border border-slate-200 rounded-full flex items-center justify-center transition-all duration-200 ${s.hover} hover:scale-110 hover:shadow-md`;
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="social-icons">
      {SOCIAL_LINKS.map(s => (
        <a
          key={s.key}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          title={s.label}
          className={itemClass(s)}
          data-testid={`social-${s.key}`}
        >
          <s.Icon size={iconSize} strokeWidth={2} />
        </a>
      ))}
      {withLabel && <span className="ml-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Follow us</span>}
    </div>
  );
}

/**
 * Floating right-side social rail.
 * Sits halfway down the viewport on desktop; collapses to a single WA button on mobile (handled by FloatingContact).
 */
export function FloatingSocialRail() {
  return (
    <div
      className="hidden lg:flex fixed right-0 top-1/2 -translate-y-1/2 z-40 flex-col gap-2 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-l-2xl shadow-lg p-2"
      data-testid="floating-social-rail"
    >
      {SOCIAL_LINKS.map(s => (
        <a
          key={s.key}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          title={s.label}
          className={`group w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 border border-slate-200 transition-all duration-200 ${s.hover}`}
          data-testid={`rail-${s.key}`}
        >
          <s.Icon size={18} strokeWidth={2} />
        </a>
      ))}
    </div>
  );
}
